import { Button, Card, Col, Empty, notification, Row, Tooltip } from 'antd';
import { OverViewCardInfo } from '../OverviewCardInfo';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';
import CallTransferingWorkflowForm from './CallTransferingWorkflowFormAdd';
import { useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import { QueryObserverResult, RefetchOptions, UseMutationResult } from '@tanstack/react-query';
import { MODAL_IDS } from 'src/enums/EModal';
import { TUTORIALS } from 'src/enums/ETutorials';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal, openModal } from '@/store/modal/slice';
import './CallTransferingWorkflowInfo.scss';
import { CallWorkflow, User } from '@/types/TAccounts';
import CallTransferingWorkflowFormEdit from './CallTransferingWorkflowFormEdit';
import { WorkflowType } from 'src/enums/EWorkflow';
import CopyableText from '@/components/LabelWithdescription/CopyableText';
import { FeatureFlaggedWorkflowAvailability } from '../WorkflowAvailability/FeatureFlaggedWorkflowAvailability';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { fetchAndUpdateWorkflowAnalysis } from '@/utils/analysisWorkflow';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import { decrementScenarioCount } from '@/store/scenarioCounts/slice';
import { DifferenceOutlined } from '@mui/icons-material';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import { safeJSONParse } from '@/utils/helper';

interface CallTransferringWorkflowProps {
	data?: User;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	debrand?: boolean;
}

export const CallTransferingWorkflowInfo: React.FC<CallTransferringWorkflowProps> = ({
	data,
	refetch,
	mutation,
	debrand,
}) => {
	const dispatch = useAppDispatch();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const callTransferingWorkflowData: CallWorkflow[] =
		typeof data?.callWorkflows === 'string'
			? safeJSONParse(data?.callWorkflows, [])
			: data?.callWorkflows || [];

	const [scenarioIndex, setScenarioIndex] = useState<number>(0);
	const [callWorkflow, setCallWorkflow] = useState<CallWorkflow | null>(null);
	const [duplicateWorkflow, setDuplicateWorkflow] = useState<CallWorkflow | null>(null);
	const canAddScenario = useAppSelector(selectCanAddScenario);
	const [isLoading, setLoading] = useState(false);

	const handleEditScenario = (index: number) => {
		setScenarioIndex(index);
		const val = callTransferingWorkflowData[index];
		setCallWorkflow(val);
		dispatch(openModal({ modalId: MODAL_IDS.CALL_TRANSFERING_WORKFLOW_EDIT_MODAL }));
	};

	const handleDuplicateScenario = (index: number) => {
		const workflowToDuplicate = callTransferingWorkflowData[index];
		workflowToDuplicate.id = undefined; // so it gets assigned a new id
		setDuplicateWorkflow(workflowToDuplicate);
		dispatch(openModal({ modalId: MODAL_IDS.CALL_TRANSFERING_WORKFLOW_DUPLICATE_MODAL }));
	};

	const handleDuplicateModalClose = () => {
		setDuplicateWorkflow(null);
		dispatch(closeModal());
	};

	const handleConfirmDelete = (index: number) => {
		ConfirmDeleteModal({
			itemName: `Scenario ${index + 1}`,
			onConfirm: () => handleDeleteScenario(index),
			title: 'Are you sure you want to delete this scenario?',
		});
	};
	const handleDeleteScenario = async (index: number) => {
		try {
			await mutation.mutateAsync({
				callWorkflows: callTransferingWorkflowData?.filter(
					(_data: CallWorkflow, i: number) => i !== index,
				),
				deleteIndex: index,
				agentId: selectedAgentId as string,
			});

			dispatch(decrementScenarioCount(WorkflowType.CALL));

			notification.success({
				message: `Scenario ${index + 1} deleted successfully.`,
			});

			refetch();
			await fetchAndUpdateWorkflowAnalysis(dispatch, selectedAgentId as string);
		} catch {
			notification.error({
				message: 'Something went wrong, please try again later!',
			});
		}
	};

	const handleDuplicateButtonClick = (index: number) => {
		if (!canAddScenario) {
			notification.error({
				message: 'Scenario limit reached',
				description: 'Max scenarios (100) reached. Please Delete existing to add more.',
			});
			return;
		}
		handleDuplicateScenario(index);
	};

	const backupNumbersComp = (backupNumbers: string[]) => {
		return (
			backupNumbers.length > 0 && (
				<div className="ml-8 flex items-center gap-2">
					Backup Numbers:
					{backupNumbers?.map((item: string, index: number) => (
						<CopyableText key={index} text={item} />
					))}
				</div>
			)
		);
	};

	const InfoComponent = () => {
		return (
			<>
				{callTransferingWorkflowData && callTransferingWorkflowData.length > 0 ? (
					callTransferingWorkflowData?.map(
						(callTransferingWorkflow: CallWorkflow, index: number) => (
							<Card
								key={index + 1}
								className="my-4"
								title={
									<div className={`${baseClass}_button-container`}>
										<div>
											<span className="font-bold text-base">{`Scenario ${index + 1}`}</span>
											<CopyableText
												text={`${callTransferingWorkflow.description}\n${callTransferingWorkflow.workflowForwardingNumber}\n${callTransferingWorkflow.voiceResponse}`}
												hideText
											/>
										</div>
										<div className="flex items-center gap-2">
											<Tooltip title="Duplicate This Scenario">
												<Button
													type="default"
													icon={<DifferenceOutlined />}
													onClick={() => handleDuplicateButtonClick(index)}
												/>
											</Tooltip>
											<Button
												type="primary"
												danger
												icon={<DeleteOutline />}
												loading={mutation.isPending && mutation.variables.deleteIndex === index}
												onClick={() => handleConfirmDelete(index)}
											>
												<div>Delete</div>
											</Button>
											<Button
												type="primary"
												icon={<EditOutlined />}
												onClick={() => handleEditScenario(index)}
											>
												<div>Edit</div>
											</Button>
											<FeatureFlaggedWorkflowAvailability
												availability={callTransferingWorkflow?.availability}
												setWorkflowAvailability={async (
													data: TWorkflowAvailabilities,
													index: number,
												) => {
													try {
														setLoading(true);
														const rawCallTransferingWorkflowData = callTransferingWorkflowData;
														rawCallTransferingWorkflowData[index].availability = data;
														await mutation.mutateAsync({
															callWorkflows: rawCallTransferingWorkflowData,
															agentId: selectedAgentId as string,
														});
														await refetch();
														notification.success({
															key: 'form-success',
															message: 'Workflow Availability has been updated!',
															duration: 3,
														});
														dispatch(closeModal());
													} catch (error: any) {
														notification.error({
															message: 'Error updating availability',
															description: error.toString(),
														});
													} finally {
														setLoading(false);
													}
												}}
												index={index}
												isLoading={isLoading}
											/>
										</div>
									</div>
								}
							>
								<>
									<div className="border-b">
										<LabelWithDescription
											customClassName="mt-2"
											label={
												<div className="flex items-center gap-1">
													Scenario Description
													<CopyableText text={callTransferingWorkflow.description} hideText />
												</div>
											}
											description="Describe the scenario in which you would like to transfer the call."
										/>
										<span className="pl-0 block w-8/12 py-2">
											{callTransferingWorkflow.description}
										</span>
									</div>
									<div className="border-b">
										<LabelWithDescription
											customClassName="mt-4"
											label={
												<div className="flex items-center gap-1">
													Phone Number
													<CopyableText
														text={callTransferingWorkflow.workflowForwardingNumber}
														hideText
													/>
												</div>
											}
											description="Specify the number you want your AI receptionist to forward the call to when the scenario is triggered."
										/>
										<div className="pl-0 block w-8/12 py-2">
											<CopyableText text={callTransferingWorkflow.workflowForwardingNumber} />
											{callTransferingWorkflow.workflowForwardingDID ? (
												<>
													<span className="!inline font-extrabold mx-[0.25rem] text-blue-700 text-base">
														#
													</span>
													<CopyableText text={callTransferingWorkflow.workflowForwardingDID} />
												</>
											) : (
												''
											)}
											{callTransferingWorkflow.backupNumbers &&
												backupNumbersComp(callTransferingWorkflow.backupNumbers)}
										</div>
									</div>
									<div className="border-b">
										<LabelWithDescription
											customClassName="mt-4"
											label={
												<div className="flex items-center gap-1">
													Voice Response
													<CopyableText text={callTransferingWorkflow.voiceResponse} hideText />
												</div>
											}
											description="Specify what you want your AI receptionist to say before the call is transferred"
										/>
										<span className="pl-0 block w-8/12 py-2">
											{callTransferingWorkflow.voiceResponse ||
												'Please hold while I transfer your call.'}
										</span>
									</div>

									<div className="border-b">
										<LabelWithDescription
											customClassName="mt-4"
											label={
												<div className="flex items-center gap-1">
													Call Whisper Settings
													<CopyableText text={callTransferingWorkflow.handoffMessage} hideText />
												</div>
											}
											description="Customize your settings for call whisper here."
										/>
										<Row className="border-b">
											<Col span={12}>
												<span className="pl-0 block w-8/12 py-2">
													{' '}
													<b>Type:</b> {callTransferingWorkflow.whisperType}
												</span>
											</Col>
											<Col span={12}>
												<span className="pl-0 block w-8/12 py-2">
													{' '}
													<b>Handoff Message Type:</b> {callTransferingWorkflow.handoffMessageType}
												</span>
											</Col>
										</Row>
										<span className="pl-0 block w-8/12 py-2">
											{callTransferingWorkflow.handoffMessage}
										</span>
									</div>
								</>
							</Card>
						),
					)
				) : (
					<Empty />
				)}
			</>
		);
	};

	const baseClass = 'call-transfering-workflow';
	return (
		<div className={baseClass}>
			<OverViewCardInfo
				width={'50%'}
				formActionType="Add"
				form={
					<CallTransferingWorkflowForm
						mutation={mutation}
						callTransferingWorkflowData={callTransferingWorkflowData || []}
						refetch={refetch}
					/>
				}
				formTitle="Add Call Transferring Workflow"
				info={<InfoComponent />}
				footer={null}
				modalId={MODAL_IDS.CALL_TRANSFERING_WORKFLOW_MODAL}
				tutorialId={TUTORIALS.CALL_TRANSFERING_WORKFLOW}
				displayTutorialActionBtn={!debrand}
			/>
			<CustomModal
				width={'50%'}
				title={<span className="font-bold">Edit Scenario</span>}
				children={
					<CallTransferingWorkflowFormEdit
						mutation={mutation}
						callWorkflow={callWorkflow}
						callTransferingWorkflowData={callTransferingWorkflowData || []}
						refetch={refetch}
						scenarioIndex={scenarioIndex}
					/>
				}
				modalId={MODAL_IDS.CALL_TRANSFERING_WORKFLOW_EDIT_MODAL}
				footer={null}
			/>
			<CustomModal
				width={'50%'}
				title={<span className="font-bold">Duplicate Scenario</span>}
				children={
					<CallTransferingWorkflowForm
						mutation={mutation}
						callTransferingWorkflowData={callTransferingWorkflowData || []}
						refetch={refetch}
						initialValues={duplicateWorkflow ? [duplicateWorkflow] : undefined}
						isDuplicate={true}
						showAddFieldButton={false}
					/>
				}
				modalId={MODAL_IDS.CALL_TRANSFERING_WORKFLOW_DUPLICATE_MODAL}
				footer={null}
				cancelAction={handleDuplicateModalClose}
				destroyOnHidden={true}
			/>
		</div>
	);
};
